// src/lib/merchantMatching.js
//
// Chunk 5 — the Auto-Match-Then-Confirm Rule (spec §5), shared across every
// transaction-creation path (QuickAdd, Receipt Scanner, Bulk Import, Log edit).
//
// The matching algorithm is deliberately dumb: EXACT equality after
// normalization (lowercase, strip punctuation, collapse whitespace). No fuzzy
// matching, no Levenshtein, no partial/substring matches. If a merchant name
// drifts ("TXU" → "TXU Energy Corp"), the user handles it manually. This
// intentionally replaces Log's older fuzzy `checkBillMatch` so behavior is
// consistent everywhere and false positives are rare.
//
// Rules (merchant_rules table, added in Chunk 1) are PER-USER — both partners
// keep their own. A rule is scoped to (user_id, merchant_pattern, target):
//   • apply_to_bill  → bill_id set   → auto-apply to that bill silently
//   • apply_to_debt  → debt_id set   → auto-apply to that debt silently
//   • never_match    → bill_id OR debt_id set (per-pair) → never ask again about
//                       THAT merchant+target pair. If the same merchant later
//                       matches a DIFFERENT bill/debt, we still ask about it.
//                       (This deviates from the library sketch's "null for
//                       never_match" in favor of design-decision 2's explicit
//                       per-pair scope.)
//
import { supabase } from './supabase'
import { emitDataEvent, EVENTS } from './dataEvents'
import { computeAccruedInterest } from '../utils/amortization'
import { recordDebtBalance } from './debtHistory'

// ─────────────────────────────────────────────────────────────
// Normalization + pure matchers
// ─────────────────────────────────────────────────────────────

// Lowercase, strip punctuation (. , - ( ) ' &), collapse whitespace, trim.
export function normalizeMerchant(name) {
  if (!name) return ''
  return String(name)
    .toLowerCase()
    .replace(/[.,\-()'&]/g, ' ')   // punctuation → space
    .replace(/\s+/g, ' ')           // collapse whitespace
    .trim()
}

// First bill whose normalized name equals the normalized transaction merchant.
// Returns { bill, matched: true } | null.
export function findBillMatch(transaction, bills) {
  const norm = normalizeMerchant(transaction?.merchant_name)
  if (!norm || !Array.isArray(bills)) return null
  for (const bill of bills) {
    if (normalizeMerchant(bill.name) === norm) return { bill, matched: true }
  }
  return null
}

// First debt whose normalized name equals the normalized transaction merchant.
// Returns { debt, matched: true } | null.
export function findDebtMatch(transaction, debts) {
  const norm = normalizeMerchant(transaction?.merchant_name)
  if (!norm || !Array.isArray(debts)) return null
  for (const debt of debts) {
    if (normalizeMerchant(debt.name) === norm) return { debt, matched: true }
  }
  return null
}

// ─────────────────────────────────────────────────────────────
// merchant_rules — load / save
// ─────────────────────────────────────────────────────────────

// Precise single-rule lookup for the single-transaction paths.
// actionType ∈ 'apply_to_bill' | 'apply_to_debt' | 'never_match'.
// For never_match the target may live in bill_id OR debt_id, so we match either
// column against the (globally unique) targetId.
export async function loadMerchantRule(userId, normalizedMerchant, actionType, targetId) {
  if (!userId || !normalizedMerchant || !targetId) return null
  let q = supabase.from('merchant_rules').select('*')
    .eq('user_id', userId)
    .eq('merchant_pattern', normalizedMerchant)
    .eq('action_type', actionType)
  if (actionType === 'apply_to_bill') q = q.eq('bill_id', targetId)
  else if (actionType === 'apply_to_debt') q = q.eq('debt_id', targetId)
  else if (actionType === 'never_match') q = q.or(`bill_id.eq.${targetId},debt_id.eq.${targetId}`)
  // .limit(1) + first row (not .maybeSingle, which throws if a duplicate rule
  // ever slipped in) — we only care whether at least one rule exists.
  const { data } = await q.limit(1)
  return (data && data[0]) || null
}

// All of a user's rules — used by Bulk Import to match in-memory (avoids a
// query per imported row).
export async function loadAllMerchantRules(userId) {
  if (!userId) return []
  const { data } = await supabase.from('merchant_rules').select('*').eq('user_id', userId)
  return data || []
}

// Pure in-memory equivalent of loadMerchantRule against a preloaded rule list.
export function matchRule(rules, normalizedMerchant, actionType, targetId) {
  if (!Array.isArray(rules)) return null
  return rules.find(r =>
    r.merchant_pattern === normalizedMerchant &&
    r.action_type === actionType &&
    (actionType === 'never_match'
      ? (r.bill_id === targetId || r.debt_id === targetId)
      : actionType === 'apply_to_bill'
        ? r.bill_id === targetId
        : r.debt_id === targetId)
  ) || null
}

// Insert a rule. targetKind ('bill' | 'debt') picks the column; it's implied by
// actionType for apply_*, and required to disambiguate never_match.
export async function saveMerchantRule(userId, normalizedMerchant, actionType, targetId, targetKind = null) {
  if (!userId || !normalizedMerchant) return null
  const row = {
    user_id: userId,
    merchant_pattern: normalizedMerchant,
    action_type: actionType,
    bill_id: null,
    debt_id: null,
  }
  const kind = targetKind || (actionType === 'apply_to_bill' ? 'bill' : actionType === 'apply_to_debt' ? 'debt' : null)
  if (kind === 'bill') row.bill_id = targetId
  else if (kind === 'debt') row.debt_id = targetId
  const { data } = await supabase.from('merchant_rules').insert(row).select().single()
  return data || null
}

// ─────────────────────────────────────────────────────────────
// Apply / unmatch
// ─────────────────────────────────────────────────────────────

// Bill: the transactions.bill_id link IS the "paid" source of truth (no
// bill_payments table in v2).
export async function applyTransactionToBill(transactionId, billId) {
  await supabase.from('transactions').update({ bill_id: billId }).eq('id', transactionId)
  emitDataEvent(EVENTS.TRANSACTION_UPDATED)
  emitDataEvent(EVENTS.BILL_UPDATED)
}

export async function unmatchTransactionFromBill(transactionId) {
  await supabase.from('transactions').update({ bill_id: null }).eq('id', transactionId)
  emitDataEvent(EVENTS.TRANSACTION_UPDATED)
  emitDataEvent(EVENTS.BILL_UPDATED)
}

// Debt: create a debt_payments row and update current_balance.
//
// Simple-mode debts (Chunk 5 behavior): 100% principal, no accrual.
// Tracked-rate debts (is_simple_mode === false, Chunk 6): accrue interest up to
// the payment date, then split the payment —
//   interest  = min(accrued, amount)      (what the payment services)
//   principal = amount − interest
//   new_balance = balance + accrued − amount
// In the normal case (amount ≥ accrued) the balance drops by exactly the
// principal; in the rare case where the payment is smaller than the accrued
// interest, principal is 0 and the unpaid interest capitalizes (balance rises
// by accrued − amount). last_interest_accrual_at advances to the payment date so
// the same window is never accrued twice.
export async function applyTransactionToDebt(transactionId, debtId, amount, paidAt = null) {
  const amt = parseFloat(amount || 0)
  if (!debtId || !(amt > 0)) return
  const when = paidAt || new Date().toISOString()

  const { data: debt } = await supabase.from('debts')
    .select('current_balance, interest_rate, is_simple_mode, last_interest_accrual_at')
    .eq('id', debtId).maybeSingle()

  const balance = debt ? parseFloat(debt.current_balance || 0) : 0
  let interestAmount = 0
  let principalAmount = amt
  let newBalance = balance - amt
  const tracked = debt && debt.is_simple_mode === false

  if (tracked) {
    const { accrued } = computeAccruedInterest(
      balance, debt.interest_rate, debt.last_interest_accrual_at, new Date(when)
    )
    interestAmount = Math.min(accrued, amt)
    principalAmount = amt - interestAmount
    newBalance = balance + accrued - amt
  }

  await supabase.from('debt_payments').insert({
    debt_id: debtId,
    transaction_id: transactionId,
    principal_amount: principalAmount,
    interest_amount: interestAmount,
    paid_at: when,
  })
  if (debt) {
    const update = { current_balance: newBalance, updated_at: new Date().toISOString() }
    if (tracked) update.last_interest_accrual_at = when
    await supabase.from('debts').update(update).eq('id', debtId)
    await recordDebtBalance(debtId, newBalance, 'payment', when)
  }
  emitDataEvent(EVENTS.TRANSACTION_UPDATED)
  emitDataEvent(EVENTS.DEBT_UPDATED)
}

// Reverse a debt application: delete the debt_payments row and add its
// principal back to the balance. No-op if there's no payment row (safe if the
// user unmatches something that was never applied).
export async function unmatchTransactionFromDebt(transactionId) {
  const { data: payRows } = await supabase.from('debt_payments')
    .select('id, debt_id, principal_amount')
    .eq('transaction_id', transactionId)
    .limit(1)
  const pay = payRows && payRows[0]
  if (!pay) return
  await supabase.from('debt_payments').delete().eq('id', pay.id)
  const { data: debt } = await supabase.from('debts').select('current_balance').eq('id', pay.debt_id).maybeSingle()
  if (debt) {
    const next = parseFloat(debt.current_balance || 0) + parseFloat(pay.principal_amount || 0)
    await supabase.from('debts').update({ current_balance: next, updated_at: new Date().toISOString() }).eq('id', pay.debt_id)
    // Reversing a payment is itself a balance change worth recording.
    await recordDebtBalance(pay.debt_id, next, 'payment')
  }
  emitDataEvent(EVENTS.TRANSACTION_UPDATED)
  emitDataEvent(EVENTS.DEBT_UPDATED)
}

// ─────────────────────────────────────────────────────────────
// Orchestration for the single-transaction paths
// ─────────────────────────────────────────────────────────────

// After a transaction is saved, decide what to do:
//   • a saved apply_* rule   → apply silently, return { status: 'applied', ... }
//   • a saved never_match rule → do nothing,   return { status: 'none' }
//   • an unmatched bill/debt hit → return { status: 'prompt', kind, target }
//   • no match at all           → return { status: 'none' }
//
// `transaction` needs { id, merchant_name, amount, transaction_date? }.
// `bills`/`debts` are the household's lists; `userId` is the acting user (rules
// are per-user).
export async function detectMatch({ transaction, bills, debts, userId }) {
  const norm = normalizeMerchant(transaction?.merchant_name)
  if (!norm) return { status: 'none' }

  const paidAt = transaction.transaction_date
    ? new Date(transaction.transaction_date + 'T12:00:00').toISOString()
    : null

  const billHit = findBillMatch(transaction, bills)
  if (billHit) {
    const bill = billHit.bill
    if (await loadMerchantRule(userId, norm, 'apply_to_bill', bill.id)) {
      await applyTransactionToBill(transaction.id, bill.id)
      return { status: 'applied', kind: 'bill', target: bill }
    }
    if (await loadMerchantRule(userId, norm, 'never_match', bill.id)) {
      return { status: 'none' }
    }
    return { status: 'prompt', kind: 'bill', target: bill }
  }

  const debtHit = findDebtMatch(transaction, debts)
  if (debtHit) {
    const debt = debtHit.debt
    if (await loadMerchantRule(userId, norm, 'apply_to_debt', debt.id)) {
      await applyTransactionToDebt(transaction.id, debt.id, transaction.amount, paidAt)
      return { status: 'applied', kind: 'debt', target: debt }
    }
    if (await loadMerchantRule(userId, norm, 'never_match', debt.id)) {
      return { status: 'none' }
    }
    return { status: 'prompt', kind: 'debt', target: debt }
  }

  return { status: 'none' }
}
